import React, { useState } from 'react';
import { Box, Button, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { integrationApiService, IntegrationConfig } from '../services/integrationApi';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useToast } from '../contexts/ToastContext';
import { tokens } from '../theme/tokens';
import { SectionCard, StatusChip } from './ui';

/** What the merchant pastes into the product page template. */
export const COMPARE_CONTAINER_SNIPPET = '<div data-motzu-compare></div>';

export interface BigCommerceCompareCardProps {
  integration: IntegrationConfig;
  /** Refetch the integration after a change. */
  onChanged: () => void;
}

/**
 * Jordan's "Storefront compare" row for a BigCommerce integration (flag
 * `product_comparison`): opt in to the `motzu.compare_rows` metafield, install
 * or remove the Script Manager script, and see where to place the container.
 * Hidden entirely while the flag is off.
 */
const BigCommerceCompareCard: React.FC<BigCommerceCompareCardProps> = ({ integration, onChanged }) => {
  const { enabled } = useOrgFeatureFlag('product_comparison');
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const [busy, setBusy] = useState<'toggle' | 'install' | 'remove' | null>(null);

  if (!enabled || integration.platform !== 'bigcommerce') return null;

  const installed = Boolean(integration.compareScriptId);
  const optedIn = integration.settings?.compareRows === true;
  const expires = integration.compareScriptTokenExpiresAt
    ? new Date(integration.compareScriptTokenExpiresAt * 1000).toLocaleDateString()
    : null;

  const run = async (kind: 'toggle' | 'install' | 'remove', action: () => Promise<unknown>, done: string | null, failed: string) => {
    setBusy(kind);
    try {
      await action();
      if (done) showToast(done, 'success');
      onChanged();
    } catch (error) {
      showToast(error instanceof Error && error.message ? error.message : failed, 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <SectionCard title={t('integrations.bigcommerceCompare.title')} subtitle={t('integrations.bigcommerceCompare.subtitle')}>
      <Stack spacing={1.5}>
        <Box>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={optedIn}
                disabled={busy !== null}
                onChange={(e) =>
                  run(
                    'toggle',
                    // The server merges settings, so only the one key is sent.
                    () => integrationApiService.updateIntegration(integration.id, { settings: { compareRows: e.target.checked } as IntegrationConfig['settings'] }),
                    null,
                    t('integrations.bigcommerceCompare.toggleFailed'),
                  )
                }
              />
            }
            label={t('integrations.bigcommerceCompare.metafieldLabel')}
          />
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{t('integrations.bigcommerceCompare.metafieldHelp')}</Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{t('integrations.bigcommerceCompare.scriptLabel')}</Typography>
          <StatusChip
            label={installed ? t('integrations.bigcommerceCompare.scriptInstalled') : t('integrations.bigcommerceCompare.scriptNotInstalled')}
            tone={installed ? 'positive' : 'neutral'}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={busy !== null}
            onClick={() =>
              run(
                'install',
                () => integrationApiService.installBigCommerceCompareScript(integration.id),
                t('integrations.bigcommerceCompare.installed'),
                t('integrations.bigcommerceCompare.installFailed'),
              )
            }
          >
            {installed ? t('integrations.bigcommerceCompare.reinstall') : t('integrations.bigcommerceCompare.install')}
          </Button>
          {installed && (
            <Button
              size="small"
              color="inherit"
              disabled={busy !== null}
              onClick={() =>
                run(
                  'remove',
                  () => integrationApiService.removeBigCommerceCompareScript(integration.id),
                  t('integrations.bigcommerceCompare.removed'),
                  t('integrations.bigcommerceCompare.removeFailed'),
                )
              }
            >
              {t('integrations.bigcommerceCompare.remove')}
            </Button>
          )}
        </Stack>
        {installed && expires && (
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{t('integrations.bigcommerceCompare.expiresOn', { date: expires })}</Typography>
        )}

        <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
          {t('integrations.bigcommerceCompare.placement')}{' '}
          <Box component="code" sx={{ fontFamily: tokens.font.mono, fontWeight: 700 }}>{COMPARE_CONTAINER_SNIPPET}</Box>
        </Typography>
      </Stack>
    </SectionCard>
  );
};

export default BigCommerceCompareCard;
